import { useMemo } from "react"
import { useQuery } from "urql"

import "../graphql/invoice.query"

import {
  GetInvoiceDocument,
  type GetInvoiceQuery,
  type GetInvoiceQueryVariables,
  type Invoice,
} from "@/graphql/codegen/graphql"

interface UseGetInvoiceParams {
  invoiceId: string | undefined
}

export interface UseGetInvoiceReturn {
  invoice: Invoice | undefined
  isFetching: boolean
  error: Error | undefined
  refetch: () => void
}

export const useGetInvoice = ({ invoiceId }: UseGetInvoiceParams): UseGetInvoiceReturn => {
  const [{ data, fetching, error }, executeQuery] = useQuery<GetInvoiceQuery, GetInvoiceQueryVariables>({
    query: GetInvoiceDocument,
    variables: { input: { invoiceId: invoiceId ?? "" } },
    pause: !invoiceId,
    requestPolicy: "cache-and-network",
  })

  const result = data?.invoice.get
  const invoice = useMemo<Invoice | undefined>(() => {
    if (!invoiceId) return undefined
    if (result?.__typename !== "InvoiceGetSuccess") return undefined
    // Guard against urql serving a stale cache hit for a different invoiceId before the new
    // request resolves — ensures the dialog never mounts with the previous invoice's data.
    if (result.invoice.invoiceId !== invoiceId) return undefined
    return result.invoice as Invoice
  }, [result, invoiceId])

  const failureError = result?.__typename === "InvoiceGetFailure" ? new Error(result.error.message) : undefined

  return {
    invoice,
    isFetching: fetching,
    error: error ?? failureError,
    refetch: () => executeQuery({ requestPolicy: "network-only" }),
  }
}
